import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ProIcon } from '../components/ProIcon';
import { PropertyCard } from '../components/PropertyCard';
import { buildWhatsAppUrl } from '../config/company';
import { MOCK_PROPERTIES, toPropertyCardFormat, USE_MOCK_DATA } from '../mock/index';
import { trackContactClick } from '../lib/analytics';

type AreaConfig = {
  slug: string;
  breadcrumb: string;
  locationLabel: string;
  heading: string;
  intro: string[];
  bullets: string[];
  inspectedLine: string;
  ctaHeading: string;
  ctaBody: string;
  ctaLabel: string;
  ctaKind: 'concierge' | 'whatsapp';
};

const AREA_CONFIGS: AreaConfig[] = [
  {
    slug: 'marbella-golden-mile',
    breadcrumb: 'Golden Mile',
    locationLabel: 'Marbella, Costa del Sol',
    heading: 'Family Rentals on the Golden Mile',
    intro: [
      'The Golden Mile runs between Marbella town and Puerto Banus - the most prestigious stretch of coastline on the Costa del Sol. Families choose it because it gives you everything within reach. Beach clubs and restaurants are walkable, while the rentals sit in calm gated settings with private pools and mature gardens.',
      'Near the action without being in the middle of it.',
    ],
    bullets: [
      'Wide walkable streets suitable for prams and young children',
      'Close to Puente Romano, Nikki Beach, and the Marbella seafront promenade',
      'Some of the highest-quality private rental stock on the Costa del Sol',
      'Easy access to Puerto Banus for dining and shopping',
      'Straightforward road access towards Malaga Airport',
    ],
    inspectedLine: 'All rentals personally visited and inspected by our team before listing.',
    ctaHeading: 'Not Sure Which Rental Fits?',
    ctaBody: 'Tell us your dates, group size, and what matters most to your family. We will shortlist the right Golden Mile rentals personally.',
    ctaLabel: 'Talk to Our Team',
    ctaKind: 'concierge',
  },
  {
    slug: 'nueva-andalucia',
    breadcrumb: 'Nueva Andalucia',
    locationLabel: 'Nueva Andalucia, Marbella',
    heading: 'Family Rentals in Nueva Andalucia',
    intro: [
      'Nueva Andalucia sits just inland from Puerto Banus - quiet, residential, and surrounded by golf courses. For families it offers something the beachfront areas often do not: genuine space. Large plots, private pools, and a calm neighbourhood feel, all within ten minutes of the coast.',
      'Supermarkets, schools, local restaurants, and sports facilities are close by. It feels like a proper home base, not a tourist zone.',
    ],
    bullets: [
      'Larger rentals with more outdoor space per euro than central Marbella',
      'Walking distance to several golf clubs',
      'Quiet streets ideal for families with young children',
      '5-10 minutes by car to Puerto Banus and the beach',
      'Local market and international shops in the area',
    ],
    inspectedLine: 'All rentals personally inspected before listing.',
    ctaHeading: 'Want Help Choosing?',
    ctaBody: 'Our team knows every rental in Nueva Andalucia personally. Tell us your dates and what you need - we will find the right match.',
    ctaLabel: 'Chat on WhatsApp',
    ctaKind: 'whatsapp',
  },
  {
    slug: 'puerto-banus',
    breadcrumb: 'Puerto Banus',
    locationLabel: 'Puerto Banus, Marbella',
    heading: 'Family Rentals Near Puerto Banus',
    intro: [
      'Puerto Banus is the most famous part of Marbella - the marina, the beach clubs, the restaurants, the energy. Staying near Puerto Banus puts everything within easy walking reach. Come home to your own private pool in the afternoon and have dinner at one of dozens of restaurants without a taxi.',
      'Best suited to families with older children or teenagers who want to be where things are happening.',
    ],
    bullets: [
      'Walking distance to Puerto Banus marina and beach',
      'Wide choice of family restaurants, beach clubs, and supermarkets nearby',
      'High-quality rentals ranging from modern apartments to large family homes',
      'Excellent road connections along the coast',
    ],
    inspectedLine: 'All rentals personally inspected before listing.',
    ctaHeading: 'Need a Hand Picking the Right Rental?',
    ctaBody: 'We know which properties give you the best access to the marina and beach without the noise. Let us shortlist the right ones for your family.',
    ctaLabel: 'Talk to Our Team',
    ctaKind: 'concierge',
  },
  {
    slug: 'elviria',
    breadcrumb: 'Elviria',
    locationLabel: 'Elviria, Marbella East',
    heading: 'Family Rentals in Elviria, Marbella East',
    intro: [
      "Elviria is one of the best-kept secrets on the Costa del Sol. Set in Marbella East, it's a pine-shaded residential area with its own beach clubs, local shops, and a pace of life that genuinely suits family holidays. Quieter than the Golden Mile, more spacious than central Marbella, and with some of the most genuinely family-friendly rental stock on the coast.",
      'The beaches here are among the cleanest and least crowded in the Marbella area. The neighbourhood is safe, easy to navigate on foot, and well served by restaurants that cater to real families.',
    ],
    bullets: [
      'Quieter, more residential feel than western Marbella',
      'Excellent family beach clubs including Nikki Beach nearby',
      'Pine-shaded streets walkable to shops and restaurants',
      'Large rentals with generous gardens and pools',
      'Approximately 15 minutes east of Marbella town by car',
    ],
    inspectedLine: 'All rentals personally inspected before listing.',
    ctaHeading: 'Elviria Is One of the Best Areas for Families.',
    ctaBody: 'Tell us your dates and what matters most - we will shortlist the right rentals personally.',
    ctaLabel: 'Chat on WhatsApp',
    ctaKind: 'whatsapp',
  },
  {
    slug: 'sierra-blanca',
    breadcrumb: 'Sierra Blanca',
    locationLabel: 'Sierra Blanca, Marbella',
    heading: 'Luxury Family Rentals in Sierra Blanca',
    intro: [
      'Sierra Blanca sits above Marbella in the foothills of La Concha mountain - one of the most exclusive and private residential areas on the entire Costa del Sol. The properties here are large, gated, and designed for complete privacy. The views across the Mediterranean are exceptional.',
      'For families seeking ultra-luxury with genuine seclusion, Sierra Blanca is unmatched. Ten minutes from central Marbella, but worlds apart from the tourist areas.',
    ],
    bullets: [
      'Gated community with 24-hour security in most urbanisations',
      'Some of the largest private rentals available in the Marbella area',
      'Panoramic sea and mountain views',
      '10 minutes by car to Marbella town and the beach',
      'Ideal for large families, multi-generational groups, and guests requiring total privacy',
    ],
    inspectedLine: 'All rentals personally inspected before listing.',
    ctaHeading: 'Sierra Blanca Rentals Are in High Demand. Check Availability Early.',
    ctaBody: 'Our local team can advise on the best properties and check your specific dates.',
    ctaLabel: 'Talk to Our Team',
    ctaKind: 'concierge',
  },
];

function getAreaConfig(pathname: string): AreaConfig | undefined {
  const slug = pathname.replace(/^\//, '');
  return AREA_CONFIGS.find((cfg) => cfg.slug === slug);
}

function matchesArea(property: (typeof MOCK_PROPERTIES)[number], slug: string): boolean {
  const city = property.city.toLowerCase();
  const textBlob = `${property.city} ${property.address} ${property.headline} ${property.description} ${property.lodging_location.join(' ')}`.toLowerCase();

  if (slug === 'nueva-andalucia') return city.includes('nueva andalucia') || city.includes('nueva andaluc');
  if (slug === 'sierra-blanca') return textBlob.includes('sierra blanca');
  if (slug === 'puerto-banus') return textBlob.includes('puerto banus') || textBlob.includes('marina');
  if (slug === 'elviria') return textBlob.includes('elviria') || textBlob.includes('marbella east');
  if (slug === 'marbella-golden-mile') return city.includes('marbella');
  return false;
}

export const AreaPage = () => {
  const { pathname } = useLocation();
  const config = getAreaConfig(pathname);

  if (!config) return null;

  const filtered = MOCK_PROPERTIES.filter((property) => matchesArea(property, config.slug));
  const cards = (filtered.length > 0 ? filtered : MOCK_PROPERTIES.slice(0, 3)).map(toPropertyCardFormat);

  return (
    <div className="min-h-screen bg-white pt-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-16">
        <nav className="text-sm text-slate-500 mb-8">
          <Link to="/" className="hover:text-brand-ocean">Home</Link> {'>'} <Link to="/destinations" className="hover:text-brand-ocean">Destinations</Link> {'>'} <span className="text-slate-700">{config.breadcrumb}</span>
        </nav>

        <section className="max-w-5xl mb-8 sm:mb-14 space-y-4 sm:space-y-6">
          <p className="text-brand-gray font-bold text-xs sm:text-sm tracking-wide uppercase">{config.locationLabel}</p>
          <h1 className="ui-page-title">{config.heading}</h1>
          <div className="space-y-4 sm:space-y-5 text-brand-gray text-sm sm:text-lg leading-relaxed">
            {config.intro.map((paragraph) => (
              <p key={paragraph}>{paragraph}</p>
            ))}
          </div>
          <div>
            <p className="font-black text-brand-ocean mb-3">Why it works:</p>
            <ul className="space-y-2 text-brand-gray">
              {config.bullets.map((bullet) => (
                <li key={bullet}>- {bullet}</li>
              ))}
            </ul>
          </div>
          <p className="text-brand-ocean font-semibold">{config.inspectedLine}</p>
        </section>

        <section className="mb-10 sm:mb-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-8">
            {cards.map((property) => (
              <PropertyCard key={property.id} property={property} />
            ))}
          </div>
        </section>

        <section className="bg-brand-sand rounded-2xl p-8 sm:p-12 text-center border border-slate-100">
          <h2 className="text-2xl sm:text-3xl font-bold text-brand-ocean mb-3">{config.ctaHeading}</h2>
          <p className="text-base text-brand-gray max-w-2xl mx-auto mb-6">{config.ctaBody}</p>
          {config.ctaKind === 'whatsapp' ? (
            <a
              href={buildWhatsAppUrl()}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackContactClick('area_cta', `${config.slug}_whatsapp`)}
              className="inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#128C7E] text-white font-black px-6 py-4 rounded-2xl transition-all duration-200 active:scale-[0.98]"
              aria-label="Chat on WhatsApp"
            >
              <ProIcon icon="whatsapp" family="brands" className="w-5 h-5" />
              <span>{config.ctaLabel}</span>
            </a>
          ) : (
            <Link
              to="/destinations"
              onClick={() => trackContactClick('area_cta', `${config.slug}_browse`)}
              className="inline-flex ui-btn-primary gap-2 group"
            >
              <span>{config.ctaLabel}</span>
              <ProIcon icon="chevron-right" family="light" className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" />
            </Link>
          )}
        </section>
      </div>
    </div>
  );
};
